import type { OnboardingStepProps } from "./Onboarding";

import React, { useState } from "react";

import Input from "@common/designSystem/Input";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useLocalStorageState } from "@editor/hooks/useLocalStorage";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import { errorToast } from "@replo/design-system/components/alert/Toast";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Slider } from "@replo/design-system/components/slider/Slider";
import { Tag } from "@replo/design-system/components/tag/Tag";
import twMerge from "@replo/design-system/utils/twMerge";
import { ArrowLeft, ChevronsLeftRight } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

import LabeledControl from "../common/designSystem/LabeledControl";
import OnboardingFooter from "./OnboardingFooter";

const TEAM_DESCRIPTION_OPTIONS = [
  {
    key: "brand",
    label: "Brand",
    imageSrc: "/images/onboarding/brand.svg",
  },
  {
    key: "dev",
    label: "Development Agency",
    imageSrc: "/images/onboarding/development-agency.svg",
  },
  {
    key: "marketing",
    label: "Marketing Agency",
    imageSrc: "/images/onboarding/marketing-agency.svg",
  },
  {
    key: "freelancer",
    label: "Freelancer",
    imageSrc: "/images/onboarding/freelancer.svg",
  },
  {
    key: "other",
    label: "Other",
    imageSrc: "/images/onboarding/other.svg",
  },
];

const TeamDescriptionOption = ({
  label,
  onClick,
  isSelected,
  imageSrc,
}: {
  label: string;
  onClick: () => void;
  isSelected: boolean;
  imageSrc: string;
}) => {
  return (
    <div
      className={twMerge(
        "border-0.5 border-border flex cursor-pointer flex-col justify-between gap-1 rounded-lg p-2 text-center",
        isSelected
          ? "border-primary bg-selectable-selected"
          : "hover:bg-button-secondary-hover",
      )}
      onClick={onClick}
    >
      <img
        src={imageSrc}
        alt={label}
        className="mx-auto h-20 w-20 object-contain"
      />
      <span
        className={twMerge(
          "text-sm",
          isSelected ? "text-default" : "text-muted",
        )}
      >
        {label}
      </span>
    </div>
  );
};

const MAX_REVENUE = 50_000_000;
const MID_POINT_REVENUE = 500_000;
const SLIDER_MAX = 100;
const SLIDER_MID_POINT = 50;

const AnnualRevenueSlider = ({
  value,
  onChange,
  isError,
}: {
  value: number | null;
  onChange: (value: number | null) => void;
  isError: boolean;
}) => {
  const rawToRevenue = (rawValue: number): number => {
    if (rawValue <= SLIDER_MID_POINT) {
      return (rawValue / SLIDER_MID_POINT) * MID_POINT_REVENUE;
    } else {
      const scale =
        (rawValue - SLIDER_MID_POINT) / (SLIDER_MAX - SLIDER_MID_POINT);
      return MID_POINT_REVENUE + scale * (MAX_REVENUE - MID_POINT_REVENUE);
    }
  };

  const revenueToRaw = (revenue: number | null): number => {
    if (revenue === null || revenue <= MID_POINT_REVENUE) {
      const effectiveRevenue = revenue ?? 0;
      return (effectiveRevenue / MID_POINT_REVENUE) * SLIDER_MID_POINT;
    } else {
      const scale =
        (revenue - MID_POINT_REVENUE) / (MAX_REVENUE - MID_POINT_REVENUE);
      return Math.min(
        SLIDER_MAX,
        SLIDER_MID_POINT + scale * (SLIDER_MAX - SLIDER_MID_POINT),
      );
    }
  };

  const formatCurrency = (value: number | null) => {
    if (value === null) {
      return "$0";
    }
    const roundedValue = Math.round(value / 1000) * 1000;

    if (roundedValue >= 1_000_000) {
      return `$${Math.round(roundedValue / 1_000_000)}M`;
    }
    if (roundedValue >= 1_000) {
      return `$${Math.round(roundedValue / 1_000)}K`;
    }
    return `$0`;
  };

  return (
    <LabeledControl
      label={
        <>
          What's your yearly revenue? <span className="text-danger">*</span>
        </>
      }
      className="text-default text-base"
      size="base"
    >
      {isError && (
        <p className="text-danger text-sm">Please select a revenue option.</p>
      )}
      <div className="flex items-center gap-2 pt-8">
        <span className="text-muted-foreground header-h4">$0</span>
        <Slider
          value={[revenueToRaw(value)]}
          max={SLIDER_MAX}
          onChange={(rawValue) => {
            onChange(rawToRevenue(rawValue[0] ?? 0));
          }}
          trackClassName="bg-gray-200"
          rangeClassName="bg-slate-800"
          thumbClassName="bg-slate-800 border-none h-8 w-8 md:h-6 md:w-6"
          thumbIcon={<ChevronsLeftRight className="h-4 w-4 text-white" />}
          thumbTooltip={
            <Tag
              size="base"
              variant={value === null ? "neutral" : "info"}
              UNSAFE_className="absolute bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap"
            >
              {value === MAX_REVENUE
                ? `${formatCurrency(value)}+`
                : formatCurrency(value)}
            </Tag>
          }
        />
        <span className="text-muted-foreground header-h4">
          {formatCurrency(MAX_REVENUE)}+
        </span>
      </div>
    </LabeledControl>
  );
};

const BasicShopInfo: React.FC<OnboardingStepProps> = ({
  currentStep,
  submitStep,
  isLoading,
  totalSteps,
}) => {
  const logEvent = useLogAnalytics();
  const { user } = useCurrentUser();
  const [firstName, setFirstName] = useLocalStorageState(
    "onboarding-firstName",
    "",
    { schema: z.string() },
  );
  const [lastName, setLastName] = useLocalStorageState(
    "onboarding-lastName",
    "",
    { schema: z.string() },
  );
  const [shopName, setShopName] = useLocalStorageState(
    "onboarding-shopName",
    "",
    { schema: z.string() },
  );
  const [teamDescription, setTeamDescription] = useLocalStorageState(
    "onboarding-teamDescription",
    null,
    { schema: z.string().nullable() },
  );
  const [annualRevenue, setAnnualRevenue] = useLocalStorageState(
    "onboarding-annualRevenue",
    null,
    { schema: z.number().nullable() },
  );
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const isFormValid =
    firstName !== "" &&
    shopName !== "" &&
    teamDescription !== null &&
    annualRevenue !== null;

  const { mutate: updateBasicShopInfo, isPending: isUpdatingBasicShopInfo } =
    trpc.onboarding.updateBasicShopInfo.useMutation();

  const [searchParams] = useSearchParams();
  const isInvited = searchParams.get("invited") === "true";

  const navigate = useNavigate();

  const handleBack = () => {
    navigate(`/onboarding/setup`);
  };

  const handleSubmit = () => {
    setHasAttemptedSubmit(true);
    if (isFormValid) {
      updateBasicShopInfo(
        {
          firstName,
          lastName,
          shopName,
          teamDescription,
          annualRevenue,
        },
        {
          onSuccess: () => {
            void trpcUtils.workspace.getUserWorkspacesList.invalidate();
            void trpcUtils.project.listWithStats.invalidate();
            void trpcUtils.user.get.invalidate();

            if (user) {
              logEvent("onboarding.flow.step.completed", {
                userId: user.email,
                stepId: "workspace-details",
                stepType: "workspace-details",
                stepData: {
                  firstName,
                  lastName,
                  shopName,
                  teamDescription,
                  annualRevenue,
                },
              });
            }
            submitStep();
          },
        },
      );
    } else {
      errorToast(
        "Unable to update shop info",
        "Please fill out all required fields",
      );
    }
  };

  return (
    <div className="flex h-full w-full flex-col items-center">
      <div className="flex h-full w-full flex-col items-center overflow-y-auto px-4 md:max-w-[50vw] md:px-0 md:pb-24">
        <div className="my-6 flex flex-col gap-1 pt-6 text-center md:mb-6 md:gap-3 md:pt-12">
          <IconButton
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={handleBack}
            variant="secondary"
            size="sm"
            UNSAFE_className="md:hidden"
            layoutClassName="h-8 w-8"
          />
          <h1 className="header-h1 text-3xl md:text-4xl">
            Let's get to know you
          </h1>
          <p className="text-muted header-h1 text-lg font-normal">
            We'll use this info to make tailored recommendations.
          </p>
        </div>

        <form className="mb-24 h-full w-full space-y-6 px-2 md:mb-0 md:flex-grow md:px-0">
          <div className="grid grid-cols-2 gap-4">
            <LabeledControl
              label={
                <>
                  First Name <span className="text-danger">*</span>
                </>
              }
              className="text-default text-sm md:text-base"
              size="base"
            >
              <Input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                size="base"
                placeholder="Bob"
                validityState={
                  hasAttemptedSubmit && firstName === "" ? "invalid" : undefined
                }
              />
            </LabeledControl>

            <LabeledControl
              label="Last Name"
              className="text-default text-sm md:text-base"
              size="base"
            >
              <Input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                size="base"
                placeholder="Builder"
              />
            </LabeledControl>
          </div>

          <LabeledControl
            label={
              <>
                What's your shop's name? <span className="text-danger">*</span>
              </>
            }
            className="text-default text-sm md:text-base"
            size="base"
          >
            <Input
              type="text"
              value={shopName}
              onChange={(e) => setShopName(e.target.value)}
              size="base"
              placeholder="Shop Name"
              validityState={
                hasAttemptedSubmit && shopName === "" ? "invalid" : undefined
              }
            />
          </LabeledControl>

          <LabeledControl
            label={
              <>
                What best describes you and your team?{" "}
                <span className="text-danger">*</span>
              </>
            }
            className="text-default text-sm md:text-base"
            size="base"
          >
            {hasAttemptedSubmit && teamDescription === null && (
              <p className="text-danger pt-1 text-sm">
                Please select an option.
              </p>
            )}
            <div className="grid grid-cols-3 grid-rows-2 gap-3 md:grid-cols-5 md:grid-rows-1">
              {TEAM_DESCRIPTION_OPTIONS.map((option) => (
                <TeamDescriptionOption
                  key={option.key}
                  label={option.label}
                  imageSrc={option.imageSrc}
                  isSelected={teamDescription === option.key}
                  onClick={() => setTeamDescription(option.key)}
                />
              ))}
            </div>
          </LabeledControl>

          <AnnualRevenueSlider
            value={annualRevenue}
            onChange={setAnnualRevenue}
            isError={hasAttemptedSubmit && annualRevenue === null}
          />
        </form>
      </div>

      <div className="absolute right-0 bottom-0 left-0">
        <OnboardingFooter
          currentStep={currentStep}
          onNext={handleSubmit}
          onBack={isInvited ? undefined : handleBack}
          isNextDisabled={
            isLoading ||
            isUpdatingBasicShopInfo ||
            (!isFormValid && hasAttemptedSubmit)
          }
          isNextLoading={isLoading || isUpdatingBasicShopInfo}
          className="md:max-w-[50vw]"
          totalSteps={totalSteps}
        />
      </div>
    </div>
  );
};

export default BasicShopInfo;
